import React, { useState, useEffect, useCallback, useRef } from 'react';
import NavigationControls from './NavigationControls';
import Slide from './Slide';
import slidesData from '../data/slidesData';

const Presentation = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const isTransitioningRef = useRef(false);
  const transitionTimeoutRef = useRef(null);
  const audioRef = useRef(null);

  const totalSlides = slidesData.length;

  // Stop any playing audio
  const stopAudio = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    setIsAudioPlaying(false);
  }, []);

  // Play audio for current slide
  const playSlideAudio = useCallback((slideIndex) => {
    stopAudio();
    const slide = slidesData[slideIndex];
    if (slide?.audioSrc) {
      const audio = new Audio(slide.audioSrc);
      audioRef.current = audio;
      audio.addEventListener('ended', () => setIsAudioPlaying(false));
      audio.addEventListener('error', () => setIsAudioPlaying(false));
      audio.play().then(() => setIsAudioPlaying(true)).catch(() => setIsAudioPlaying(false));
    }
  }, [stopAudio]);

  const goToSlide = useCallback((index) => {
    if (index >= 0 && index < totalSlides && !isTransitioningRef.current) {
      isTransitioningRef.current = true;
      stopAudio();
      
      // Clear any existing timeout
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      
      setCurrentSlide(index);
      
      transitionTimeoutRef.current = setTimeout(() => {
        isTransitioningRef.current = false;
        playSlideAudio(index);
      }, 600);
    }
  }, [totalSlides, stopAudio, playSlideAudio]);

  const nextSlide = useCallback(() => {
    if (currentSlide < totalSlides - 1) {
      goToSlide(currentSlide + 1);
    }
  }, [currentSlide, totalSlides, goToSlide]);

  const prevSlide = useCallback(() => {
    if (currentSlide > 0) {
      goToSlide(currentSlide - 1);
    }
  }, [currentSlide, goToSlide]);
  
  // Cleanup timeout and audio on unmount
  useEffect(() => {
    return () => {
      if (transitionTimeoutRef.current) {
        clearTimeout(transitionTimeoutRef.current);
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  // Keyboard navigation (hidden - no visual indicators)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Home') {
        e.preventDefault();
        goToSlide(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        goToSlide(totalSlides - 1);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, goToSlide, totalSlides]);

  // Prevent scroll and wheel events
  useEffect(() => {
    const preventScroll = (e) => {
      e.preventDefault();
    };

    const preventWheel = (e) => {
      e.preventDefault();
    };

    // Prevent all scroll-related events
    window.addEventListener('wheel', preventWheel, { passive: false });
    window.addEventListener('touchmove', preventScroll, { passive: false });
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('wheel', preventWheel);
      window.removeEventListener('touchmove', preventScroll);
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // Prevent context menu for cleaner presentation
  useEffect(() => {
    const preventContextMenu = (e) => {
      e.preventDefault();
    };
    window.addEventListener('contextmenu', preventContextMenu);
    return () => window.removeEventListener('contextmenu', preventContextMenu);
  }, []);

  // Toggle play/pause for current slide
  const toggleAudio = useCallback(() => {
    if (isAudioPlaying) {
      stopAudio();
    } else {
      playSlideAudio(currentSlide);
    }
  }, [isAudioPlaying, stopAudio, playSlideAudio, currentSlide]);

  const hasAudio = slidesData[currentSlide]?.audioSrc;

  return (
    <div className="presentation-container" data-testid="presentation-container">
      {/* Subtle grain overlay for premium feel */}
      <div className="grain-overlay" />

      {/* Play/Pause button — always visible if slide has audio */}
      {hasAudio && (
        <button
          onClick={toggleAudio}
          data-testid="audio-play-btn"
          className="fixed top-6 right-6 z-50 flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.06] border border-white/[0.08] backdrop-blur-sm cursor-pointer hover:bg-white/[0.1] transition-colors"
        >
          {isAudioPlaying ? (
            <>
              <div className="flex items-end gap-[2px] h-3">
                <div className="w-[2px] bg-[#0068ff] rounded-full animate-audio-bar1" />
                <div className="w-[2px] bg-[#0068ff] rounded-full animate-audio-bar2" />
                <div className="w-[2px] bg-[#0068ff] rounded-full animate-audio-bar3" />
                <div className="w-[2px] bg-[#0068ff] rounded-full animate-audio-bar4" />
              </div>
              <span className="text-white/40 text-[10px] tracking-wider uppercase">Pause</span>
            </>
          ) : (
            <>
              <svg viewBox="0 0 24 24" className="w-3 h-3 text-[#0068ff]" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
              <span className="text-white/40 text-[10px] tracking-wider uppercase">Play</span>
            </>
          )}
        </button>
      )}

      {/* Slides */}
      {slidesData.map((slide, index) => (
        <Slide 
          key={slide.id} 
          slide={slide} 
          isActive={index === currentSlide}
        />
      ))}

      {/* Navigation Controls - Only back and forward buttons */}
      <NavigationControls
        currentSlide={currentSlide}
        totalSlides={totalSlides}
        onPrev={prevSlide}
        onNext={nextSlide}
      />
    </div>
  );
};

export default Presentation;
